import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Avatar, Box, Card, TextField, Typography, IconButton, Tooltip, Popover } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CalendarToday, Close, Check } from '@mui/icons-material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast } from 'react-toastify';
import { stringAvatar } from '../../../../utils/avatarCreator';
import { createTask, getAllFilteredTask, getAllTasksByProjectId } from '../../../../services/taskService';
import { getSectionsByProjectId } from '../../../../services/sectionService';
import { isAdmin, isClient, isStakeholder } from '../../../../utils/share';
import { useProject } from '../../Layout/header/context';
import { Loader } from '../../../../components/loader';

const AddTaskForm = ({
  sectionId,
  projectId,
  onCancel,
  onSubmit,
  defaultAssignToSelf = false,
  projectDetails = null,
  showAssigneeDropdown = false,
  canAddParentTask = false,
}) => {
  const [taskName, setTaskName] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  // defaults: dueDate tomorrow, plannedHours 8 for non-stakeholders.
  const [dueDate, setDueDate] = useState(dayjs().add(1, 'day'));
  const [plannedHours, setPlannedHours] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [issueType, setIssueType] = useState('');
  const [isParent, setIsParent] = useState(false);

  const formRef = useRef(null);
  const dateButtonRef = useRef(null);

  const accountData = useSelector((state) => state?.login?.user);
  const { projectDetails: contextProjectDetails, stakeholderOverviewVisibility } = useProject();

  const finalProjectId = projectId || contextProjectDetails?.id;
  const finalProjectDetails = projectDetails || contextProjectDetails;

  const projectMembers = finalProjectDetails?.projectmember || [];
  const employeeList = projectMembers.map((member) => ({
    id: member.userId,
    userName: member.user?.userName || 'Unknown User',
  }));

  // convenience flag (call once per render)
  const stakeholder = isStakeholder() || isClient();
  const canShowAssigneeDropdown = showAssigneeDropdown && (!stakeholder || stakeholderOverviewVisibility?.projectMembers !== false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!taskName.trim()) {
      toast.error('Task name is required');
      return;
    }
    if (!assignedUserId && !isParent) {
      toast.error('Please assign the task to someone');
      return;
    }
    // If not stakeholder, validate plannedHours. Stakeholders don't need to set plannedHours.
    if (!stakeholder && !plannedHours) {
      toast.error('Please enter a valid planned hours (minimum 1 hour)');
      return;
    }
    if (plannedHours === '0') {
      toast.error('Planned hours cannot be 0');
      return;
    }

    try {
      setIsSubmitting(true);

      // compute effective values to be used in payloads
      const effectivePlannedHours = stakeholder ? 0 : parseFloat(plannedHours, 10);
      const effectiveDueDate = stakeholder ? dayjs().add(1, 'day') : dueDate;

      // If stakeholder, enforce issue defaults and resolve parent "Not Aware" in "To Do"
      if (stakeholder) {
        // 1) Find "To Do" section id for this project
        const sectionsRes = await getSectionsByProjectId(finalProjectId, { pageNumber: 0 });
        const allSections = sectionsRes?.data?.data?.rows || [];
        const toDoSection = allSections.find((s) => (s?.name || '').toLowerCase() === 'to do');
        if (!toDoSection) {
          throw new Error("'To Do' section not found in this project. Please create it first.");
        }

        // 2) Try to find existing parent task named exactly "Not Aware" within this project
        let parentTaskId;
        try {
          // Prefer project-scoped API to avoid cross-project matches
          const listRes = await getAllTasksByProjectId({
            projectId: finalProjectId,
            pageSize: 9999,
            filters: JSON.stringify([]),
            keyword: 'Not Aware',
            sorting: JSON.stringify([]),
          });
          const projectRows = listRes?.data?.rows || listRes?.rows || [];
          const exactMatch = projectRows.find((t) => (t?.taskName || '').trim().toLowerCase() === 'not aware');
          if (exactMatch) {
            parentTaskId = exactMatch.id;
          }
        } catch (_) {
          // ignore search errors; we'll create if not found
        }

        // 3) Create parent if not found
        if (!parentTaskId) {
          const today = dayjs();
          const tomorrow = dayjs().add(1, 'day');
          const parentPayload = {
            taskName: 'Not Aware',
            userId: null,
            sectionId: toDoSection.id,
            endDate: tomorrow.format('YYYY-MM-DD'),
            startDate: today.format('YYYY-MM-DD'),
            status: 'Not Started',
            projectId: finalProjectId,
            plannedHours: 1,
            isBillable: false,
            taskType: 'Task',
            isParentTask: true,
          };
          try {
            const parentRes = await createTask(parentPayload);
            parentTaskId = parentRes?.data?.data?.id || parentRes?.data?.id;
          } catch (createErr) {
            // If creation failed (possibly due to existing), re-query and pick existing
            const recheck = await getAllTasksByProjectId({
              projectId: finalProjectId,
              pageSize: 9999,
              filters: JSON.stringify([]),
              globalFilter: 'Not Aware',
              sorting: JSON.stringify([]),
            });
            const rows = recheck?.data?.rows || recheck?.rows || [];
            const existing = rows.find((t) => (t?.taskName || '').trim().toLowerCase() === 'not aware');
            if (existing) parentTaskId = existing.id;
          }
          if (!parentTaskId) throw new Error('Failed to create/find parent task "Not Aware"');
        }

        // 4) Create the stakeholder issue task with enforced defaults
        const issuePayload = {
          taskName: taskName.trim(),
          userId: parseInt(assignedUserId, 10),
          sectionId, // stays where user clicked (Issues section)
          endDate: effectiveDueDate.format('YYYY-MM-DD'),
          status: 'Not Started',
          projectId: finalProjectId,
          plannedHours: effectivePlannedHours,
          isBillable: false,
          taskType: 'Issue',
          issueType: issueType || 'Low',
          parentTaskId,
        };
        await createTask(issuePayload);
      } else {
        // Non-stakeholders: keep existing behavior - using effective values computed above
        const taskData = {
          taskName: taskName.trim(),
          userId: isParent ? null : parseInt(assignedUserId, 10),
          sectionId,
          endDate: effectiveDueDate.format('YYYY-MM-DD'),
          status: 'Not Started',
          projectId: finalProjectId,
          plannedHours: effectivePlannedHours,
          isBillable: true,
          taskType: 'Task',
          isParentTask: isParent,
        };
        await createTask(taskData);
      }

      toast.success('Task created successfully');

      // Reset form values to defaults
      setTaskName('');
      setAssignedUserId('');
      setDueDate(dayjs().add(1, 'day'));
      setPlannedHours(1);

      if (onSubmit) onSubmit();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to create task. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    if (isParent) {
      setAssignedUserId('');
    }
  }, [isParent]);

  const handleCancel = useCallback(() => {
    setTaskName('');
    setAssignedUserId('');
    setDueDate(dayjs().add(1, 'day'));
    setPlannedHours(1);
    setUserAnchorEl(null);
    setIsDatePickerOpen(false);

    if (onCancel) onCancel();
  }, [onCancel]);

  const handleUserClick = (event) => setUserAnchorEl(event.currentTarget);
  const handleUserClose = () => setUserAnchorEl(null);
  const handleUserSelect = (userId) => {
    setAssignedUserId(userId);
    setUserAnchorEl(null);
  };

  const calculateDueDateFromHours = (hours) => {
    const hoursNum = parseInt(hours, 10);
    if (!hoursNum || hoursNum < 1) return dayjs().add(1, 'day');
    return dayjs().add(hoursNum, 'hour');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // For stakeholders plannedHours not required
      if (taskName.trim() && assignedUserId && (stakeholder || (plannedHours && plannedHours >= 1))) {
        handleSubmit(e);
      } else {
        toast.error('Please fill in task name, assign to someone, and enter valid planned hours');
      }
    }
  };

  const selectedUser = employeeList.find((emp) => emp.id === parseInt(assignedUserId, 10));

  // Auto-scroll to form when it mounts
  useEffect(() => {
    const scrollToForm = () => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    const timeoutId1 = setTimeout(scrollToForm, 100);
    return () => clearTimeout(timeoutId1);
  }, []);

  useEffect(() => {
    if (defaultAssignToSelf && accountData?.id && !assignedUserId) {
      setAssignedUserId(accountData.id.toString());
    }
  }, [defaultAssignToSelf, accountData?.id, assignedUserId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        formRef.current &&
        !formRef.current.contains(event.target) &&
        !event.target.closest('.MuiPopover-root') &&
        !event.target.closest('.MuiPickersPopper-root') &&
        !event.target.closest('.MuiDialog-root') &&
        !event.target.closest('[role="dialog"]')
      ) {
        handleCancel();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleCancel]);

  return (
    <Card
      ref={formRef}
      data-add-task-form
      sx={{ mt: 1, mb: 1, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TextField
            placeholder="Enter task name..."
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            fullWidth
            size="small"
            autoFocus
            disabled={isSubmitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
              },
            }}
          />

          {/* Tick Icon */}
          {canAddParentTask && (
            <Tooltip title="Is Parent Task">
              <IconButton
                onClick={() => setIsParent((prev) => !prev)}
                disabled={isSubmitting}
                sx={{
                  color: isParent ? 'success.main' : 'grey.400',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: isParent ? 'success.dark' : 'grey.600',
                  },
                }}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {isSubmitting && (
            <Box sx={{ width: '20px', height: '20px', flexShrink: 0 }}>
              <Loader size={20} />
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* User Assignment - Only show for admin users */}
            {canShowAssigneeDropdown && !isParent && (
              <Tooltip title={selectedUser?.userName || 'Assign task'} placement="top" arrow>
                <IconButton
                  onClick={handleUserClick}
                  size="small"
                  disabled={isSubmitting}
                  sx={{ width: 32, height: 32, border: '1px solid #ddd' }}
                >
                  {selectedUser ? (
                    <Avatar {...stringAvatar(selectedUser.userName)} sx={{ width: 20, height: 20, fontSize: '10px' }} />
                  ) : (
                    <Typography sx={{ fontSize: '12px', color: '#666' }}>+</Typography>
                  )}
                </IconButton>
              </Tooltip>
            )}

            {/* Issue Priority dropdown - Only for stakeholders */}
            {stakeholder && (
              <Tooltip title="Issue Priority" placement="top" arrow>
                <TextField
                  select
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: 90,
                    '& .MuiInputBase-root': {
                      height: 32,
                      fontSize: '13px',
                      padding: 0,
                    },
                    '& .MuiSelect-select': {
                      padding: '4px 8px',
                    },
                  }}
                  SelectProps={{
                    native: true,
                  }}
                  disabled={isSubmitting}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </TextField>
              </Tooltip>
            )}

            {/* Due Date & Planned Hours: hidden for stakeholders */}
            {!stakeholder && (
              <>
                <Tooltip title="Due date" placement="top" arrow>
                  <IconButton
                    ref={dateButtonRef}
                    size="small"
                    onClick={() => setIsDatePickerOpen(true)}
                    disabled={isSubmitting}
                    sx={{ width: 32, height: 32, border: '1px solid #ddd' }}
                  >
                    <CalendarToday sx={{ fontSize: 16, color: '#666' }} />
                  </IconButton>
                </Tooltip>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    value={dueDate}
                    onChange={(newDate) => {
                      setDueDate(newDate);
                      setIsDatePickerOpen(false);
                    }}
                    minDate={dayjs()}
                    open={isDatePickerOpen}
                    onClose={() => setIsDatePickerOpen(false)}
                    PopperProps={{
                      anchorEl: dateButtonRef.current,
                      placement: 'bottom-start',
                    }}
                    renderInput={(params) => <TextField {...params} sx={{ display: 'none' }} />}
                  />
                </LocalizationProvider>

                <Tooltip title="Planned hours" placement="top" arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 50,
                    }}
                  >
                    <TextField
                      type="number"
                      value={plannedHours}
                      label="Hrs"
                      onChange={(e) => {
                        const newHours = e.target.value;
                        setPlannedHours(newHours);
                        setDueDate(calculateDueDateFromHours(newHours));
                      }}
                      size="small"
                      disabled={isSubmitting}
                      sx={{
                        '& .MuiInputBase-root': {
                          width: 55,
                          height: 30,
                          fontSize: '13px',
                          textAlign: 'center',
                        },
                        '& input': {
                          textAlign: 'center',
                          padding: '4px 0',
                        },
                      }}
                      inputProps={{ min: 1, max: 999 }}
                    />
                  </Box>
                </Tooltip>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Add Task" placement="top" arrow>
              <IconButton
                size="small"
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !taskName.trim() ||
                  !assignedUserId ||
                  (!stakeholder && (!plannedHours || plannedHours < 0))
                }
                sx={{
                  width: 32,
                  height: 32,
                  color: 'white',
                  backgroundColor: '#4caf50',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#666',
                  },
                }}
              >
                <Check sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Cancel" placement="top" arrow>
              <IconButton
                size="small"
                onClick={handleCancel}
                disabled={isSubmitting}
                sx={{ width: 32, height: 32, color: '#666' }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* User Selection Popover - Only show for admin users */}
      {canShowAssigneeDropdown && (
        <Popover
          open={Boolean(userAnchorEl)}
          anchorEl={userAnchorEl}
          onClose={handleUserClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1, minWidth: 150 }}>
            {employeeList.map((employee) => (
              <Box
                key={employee.id}
                onClick={() => handleUserSelect(employee.id)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, cursor: 'pointer' }}
              >
                <Avatar {...stringAvatar(employee.userName)} sx={{ width: 24, height: 24, fontSize: '11px' }} />
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#333' }}>
                  {employee.userName}
                </Typography>
              </Box>
            ))}
          </Box>
        </Popover>
      )}
    </Card>
  );
};

AddTaskForm.propTypes = {
  sectionId: PropTypes.number.isRequired,
  projectId: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  defaultAssignToSelf: PropTypes.bool,
  projectDetails: PropTypes.object,
  showAssigneeDropdown: PropTypes.bool,
};

export default AddTaskForm;
